import { useCallback } from 'react';

import { useFilters } from 'context/filtersContext';
import { useGlobalKolPlanningContext } from 'context/globalKolPlanningContext';

import { adaptGkpLegacyRequest } from './gkpV2LegacyAdapters';
import { useApi } from './useApi';

export const useGkpApi = () => {
  const { call, loading } = useApi();
  const { filters } = useFilters();

  const { projectSecret, projectParams, roleTeamName } = useGlobalKolPlanningContext();

  const groupedFilters = useCallback(
    resetFilter => {
      return Object.keys(filters || {}).reduce((obj, filter) => {
        if (filter === resetFilter) return obj;

        const [key, name] = filter.split('_');
        const groupKey = `${key}Filters`;

        if (!name) {
          return {
            ...obj,
            [key]: filters[key],
          };
        }

        return {
          ...obj,
          [groupKey]: [
            ...(obj?.[groupKey] || []),
            { columnName: name, reference: filters[filter] },
          ],
        };
      }, {});
    },
    [filters],
  );

  const gkpCall = useCallback(
    async ({
      url,
      version,
      data,
      secret,
      responseType,
      applyProjectParams = true,
      resetFilter,
    }) => {
      if (!(projectSecret || secret)) {
        throw new Error(`No project secret found for ${url}; projectSecret/secret has not loaded`);
      }

      const request = adaptGkpLegacyRequest({
        method: 'POST',
        url,
        version,
        responseType,
        data: {
          ...data,
          filters: groupedFilters(resetFilter),
          key: projectSecret || secret,
          paramaters: applyProjectParams ? projectParams : undefined,
          roleTeamName,
        },
      });

      const response = await call(request.request);

      return request.adaptResponse(response);
    },
    [call, groupedFilters, projectParams, projectSecret, roleTeamName],
  );

  return { call: gkpCall, loading, groupedFilters };
};
